import { useSyncExternalStore } from 'react'

/**
 * The smallest thing that counts as routing: a path to read, and a way to
 * change it without a reload.
 *
 * Deliberately not a router. Gameplay stays at `/`; the path only selects the
 * development workbench or accepts a legacy place link on initial entry.
 */

/**
 * `pushState` fires no event, so a click that changes the path would leave
 * every subscriber showing the old one. This is the event we fire ourselves;
 * `popstate` covers the back button, which the browser does announce.
 */
const NAVIGATED = 'stageville:navigated'

function subscribe(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange)
  window.addEventListener(NAVIGATED, onChange)
  return () => {
    window.removeEventListener('popstate', onChange)
    window.removeEventListener(NAVIGATED, onChange)
  }
}

/** The current path, re-read whenever it changes. */
export function usePath(): string {
  return useSyncExternalStore(
    subscribe,
    () => window.location.pathname,
    () => '/',
  )
}

/**
 * Go somewhere in the app without reloading it. `replace` is for an address
 * that only ever forwards, so the back button skips it rather than bouncing
 * off it again.
 */
export function navigate(destination: string, { replace = false }: { replace?: boolean } = {}): void {
  if (replace) window.history.replaceState({}, '', destination)
  else window.history.pushState({}, '', destination)
  window.dispatchEvent(new Event(NAVIGATED))
}

/**
 * True for the kind of click that means "go there, in this tab".
 *
 * Everything else is someone asking the browser for something we should not
 * take away from them: a new tab, a new window, a download.
 */
export function isPlainLeftClick(event: {
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  defaultPrevented: boolean
}): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !event.defaultPrevented
  )
}
